import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import { Button, Layout, Modal, Text } from 'ds';
import { PANTRY_ORDERS_PATH } from 'routes/paths';
import { GRAY_700_HEX, formatDate } from 'shared';
import { actions } from 'store/Consumables';
import { useAppDispatch } from 'store/hooks';

import { getConsumablesOrders, getConsumablesProductSubscriptions, skipOrder } from './requests';

interface ConsumablesSkipOrderModalProps {
  isVisible: boolean;
  onClose: () => void;
  orderId: number;
  nextOrdersDeliveryDate: string;
}

const ConsumablesSkipOrderModal: React.FC<ConsumablesSkipOrderModalProps> = ({
  isVisible,
  onClose,
  orderId,
  nextOrdersDeliveryDate
}) => {
  const history = useHistory();
  const { isMobile } = useContext(media);
  const dispatch = useAppDispatch();
  const [isSkipping, setIsSkipping] = useState(false);

  const nextDeliveryDateFromNowFormatted = formatDate({
    date: DateTime.fromISO(nextOrdersDeliveryDate),
    format: 'MED'
  });
  const eightDaysBeforeNextDeliveryDate = DateTime.fromISO(nextOrdersDeliveryDate).minus({ days: 8 });
  const eightDaysBeforeNextDeliveryDateFormatted = formatDate({
    date: eightDaysBeforeNextDeliveryDate,
    format: 'MED'
  });

  const handleSkipOrder = async () => {
    setIsSkipping(true);

    try {
      const response = await skipOrder(orderId);

      if (response.status === 200) {
        // Refresh the data to get updated orders and subscriptions
        const [ordersResponse, subscriptionsResponse] = await Promise.all([
          getConsumablesOrders(),
          getConsumablesProductSubscriptions()
        ]);

        // Update the store with fresh data
        dispatch(actions.setConsumablesOrders(ordersResponse.data));
        dispatch(actions.setConsumablesProductSubscriptions(subscriptionsResponse.data));

        onClose();
        history.push(PANTRY_ORDERS_PATH);
        showSnackbar({
          message: 'Next order is now ' + nextDeliveryDateFromNowFormatted
        });
      } else {
        showSnackbar({
          message: 'Failed to skip order',
          negative: true
        });
      }
    } catch (error) {
      console.error('Error skipping order:', error);
      showSnackbar({
        message: 'Something went wrong while skipping the order',
        negative: true
      });
    } finally {
      setIsSkipping(false);
    }
  };

  return (
    <Modal
      isVisible={isVisible}
      isBottomSheet={isMobile}
      bottomSheetIsRounded={isMobile}
      bottomSheetGutterTop={350}
      onClose={onClose}
      width={500}
      header="Skip this order?"
      headerAlign="left"
      paddingX={0}
      footer={
        <Layout direction="row" gap={isMobile ? 12 : 16} justify="flex-end" paddingX={isMobile ? 0 : 24} width="100%">
          <Layout width={isMobile ? '50%' : undefined}>
            <Button fullWidth={isMobile} type="secondaryOutline" size="sm" text="Cancel" onClick={onClose} />
          </Layout>
          <Layout width={isMobile ? '50%' : undefined}>
            <Button
              fullWidth={isMobile}
              type="primary"
              size="sm"
              text={isSkipping ? 'Skipping...' : 'Confirm'}
              onClick={handleSkipOrder}
              disabled={isSkipping}
            />
          </Layout>
        </Layout>
      }
    >
      <Layout direction="column" gap={16} paddingX={isMobile ? 24 : 36}>
        <Text size="body-sm" color="gray-700">
          Your next order will process{' '}
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.02em',
              color: `${GRAY_700_HEX}`
            }}
          >
            {eightDaysBeforeNextDeliveryDateFormatted}
          </span>{' '}
          to be delivered{' '}
          <span
            style={{
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '20px',
              letterSpacing: '0.02em',
              color: `${GRAY_700_HEX}`
            }}
          >
            {nextDeliveryDateFromNowFormatted}
          </span>
          .
        </Text>
        <Text size="body-sm" color="gray-700">
          Don’t worry — your pantry subscription will remain active.
        </Text>
      </Layout>
    </Modal>
  );
};

export default ConsumablesSkipOrderModal;
